'use client';

import * as React from 'react';
import { cn } from './cn';
import { CONTROL_BASE, CONTROL_SIZE, CONTROL_TEXT, Field, type ControlSize } from './Field';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: ControlSize;
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  /** Adornment at the reading start (a search glyph, a currency, …). */
  startIcon?: React.ReactNode;
  /** Adornment at the reading end — commonly a clear/reveal IconButton. */
  endIcon?: React.ReactNode;
  /** Class for the outer <div> when label/hint/error are used. */
  containerClassName?: string;
}

/**
 * Text input. Forces `dir="auto"` unless overridden, so a Hebrew album title
 * and an English URL each align themselves correctly inside the same form.
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    size = 'md',
    label,
    hint,
    error,
    startIcon,
    endIcon,
    className,
    containerClassName,
    dir = 'auto',
    id: idProp,
    required,
    ...props
  },
  ref,
) {
  const render = (ids: { id: string; describedBy?: string; invalid: boolean }) => (
    <div className="relative flex items-center">
      {startIcon ? (
        <span className="pointer-events-none absolute start-3 flex text-ink-3 [&>svg]:size-4">
          {startIcon}
        </span>
      ) : null}
      <input
        ref={ref}
        id={idProp ?? ids.id}
        dir={dir}
        required={required}
        aria-invalid={ids.invalid || undefined}
        aria-describedby={ids.describedBy}
        className={cn(
          CONTROL_BASE,
          CONTROL_TEXT,
          CONTROL_SIZE[size],
          startIcon && 'ps-9',
          endIcon && 'pe-10',
          className,
        )}
        {...props}
      />
      {endIcon ? (
        <span className="absolute end-2 flex items-center text-ink-3 [&>svg]:size-4">{endIcon}</span>
      ) : null}
    </div>
  );

  if (!label && !hint && !error) {
    const bare = render({ id: idProp ?? '', describedBy: props['aria-describedby'], invalid: false });
    /*
     * A `containerClassName` without a label used to be dropped on the floor: there was no `Field`
     * to carry it, so the class silently did nothing. The editor's title field asked for
     * `min-w-16 max-w-64 flex-1` that way and got none of it — on a 390 px phone the album's name
     * was crushed to 26 px, narrower than a single character. A prop that quietly does nothing is
     * worse than one that does not exist, so it gets a container of its own when it is given.
     */
    return containerClassName ? <div className={containerClassName}>{bare}</div> : <>{bare}</>;
  }
  return (
    <Field label={label} hint={hint} error={error} required={required} className={containerClassName}>
      {(ids) => render({ ...ids, describedBy: ids.describedBy })}
    </Field>
  );
});
